import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import FileUpload from "@/components/FileUpload";
import BiometricCapture from "@/components/BiometricCapture";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  Loader2,
  ShieldCheck,
  IdCard,
  Camera,
  CheckCircle2,
  Clock,
  XCircle,
  User,
  Upload,
} from "lucide-react";

const initialAddress = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
};

const STEP_LABELS = ["Personal info", "ID document", "Selfie", "Review"];

export default function VerifyIdentity() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [step, setStep] = useState(0);
  const [dob, setDob] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState({ ...initialAddress });

  const [idDoc, setIdDoc] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [livenessFrames, setLivenessFrames] = useState([]);
  const [livenessPassed, setLivenessPassed] = useState(false);
  const [selfieMode, setSelfieMode] = useState("biometric"); // biometric | upload

  const [submitting, setSubmitting] = useState(false);
  const [kycState, setKycState] = useState(null);

  useEffect(() => {
    if (!user) return;
    // Poll KYC status on load — redirect if already approved
    api
      .get("/kyc/status")
      .then(({ data }) => {
        setKycState(data);
        if (data.kyc_status === "approved") {
          // Already verified — just stay and show the success state
        }
      })
      .catch(() => {});
  }, [user]);

  const setAddr = (k, v) => setAddress({ ...address, [k]: v });

  const validatePersonal = () => {
    if (!dob) return "Please enter your date of birth.";
    const d = new Date(dob);
    const age = (Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
    if (Number.isNaN(age) || age < 18) return "You must be at least 18 years old.";
    if (age > 120) return "Please enter a valid date of birth.";
    if (!phone || phone.trim().length < 5) return "Please enter your phone number.";
    if (
      !address.line1 ||
      !address.city ||
      !address.state ||
      !address.postal_code ||
      !address.country
    )
      return "Please complete your home address.";
    return null;
  };

  const next = () => setStep((s) => Math.min(s + 1, STEP_LABELS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!selfie?.data) {
      toast.error("Please capture or upload a selfie.");
      return;
    }
    if (!idDoc?.data) {
      toast.error("Please upload your government ID document.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/kyc/submit", {
        date_of_birth: dob,
        phone: phone.trim(),
        address,
        photo: selfie,
        id_document: idDoc,
        liveness_frames: livenessFrames,
        liveness_passed: livenessPassed,
      });
      setKycState({
        kyc_status: data.kyc_status,
        face_match: data.face_match,
      });
      await refreshUser();
      if (data.auto_approved) {
        // Should not happen post-Feb 2026 policy — retained for defensive UX only
        toast.success("Verified!");
      } else {
        toast.success("KYC submitted. We'll review it within 24 hours.");
      }
      // Bump to Review step (shows outcome)
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  // If already approved — dedicated success view
  if (kycState?.kyc_status === "approved") {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto text-center py-16">
          <CheckCircle2 className="w-14 h-14 text-[#7BBF8E] mx-auto" />
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white mt-4">
            Identity verified
          </h1>
          <p className="text-white/70 mt-3">
            You now have full access to deposits, transfers, withdrawals, and
            crypto.
          </p>
          <Button
            onClick={() => navigate("/app")}
            className="mt-6 bg-[#B58957] hover:bg-[#9C7548] text-[#0A130E]"
            data-testid="verify-goto-dashboard"
          >
            Go to dashboard <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </AppLayout>
    );
  }

  // If under review — dedicated pending view
  if (kycState?.kyc_status === "submitted" && step !== 3) {
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto text-center py-16">
          <Clock className="w-14 h-14 text-[#B58957] mx-auto" />
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white mt-4">
            Under review
          </h1>
          <p className="text-white/70 mt-3 max-w-md mx-auto">
            Thanks — your documents are being reviewed by our compliance team.
            You'll receive an email once your identity is verified (usually
            within 24 hours).
          </p>
          <Button
            onClick={() => navigate("/app")}
            variant="ghost"
            className="mt-6 text-white/80 hover:bg-white/[0.06]"
            data-testid="verify-goto-dashboard-pending"
          >
            Return to dashboard
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-3xl">
        <div className="text-eyebrow text-white/55">Identity verification</div>
        <div className="flex items-center gap-3 mt-1">
          <ShieldCheck className="w-7 h-7 text-[#B58957]" />
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white">
            Verify your identity
          </h1>
        </div>
        <p className="text-white/65 mt-3 max-w-2xl">
          To unlock deposits, transfers, and crypto, we need to confirm who you
          are. It takes about 2 minutes — you'll just need a photo of your
          government ID and a quick selfie.
        </p>

        {/* Progress dots */}
        <div className="mt-8 flex gap-2">
          {STEP_LABELS.map((label, i) => (
            <div key={label} className="flex-1">
              <motion.div
                className={`h-1 rounded-full ${
                  i <= step ? "bg-[#B58957]" : "bg-white/10"
                }`}
                animate={{ scaleX: i <= step ? 1 : 0.4 }}
                style={{ transformOrigin: "left" }}
              />
              <div
                className={`mt-2 text-xs ${
                  i === step ? "text-white font-medium" : "text-white/50"
                }`}
                data-testid={`verify-step-label-${i}`}
              >
                {label}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 fv-card p-6 md:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={`step-${step}`}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.25 }}
            >
              {step === 0 && (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 text-white/85">
                    <User className="w-4 h-4 text-[#B58957]" />
                    <span className="font-display">Personal info</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-white/60">
                        Date of birth
                      </Label>
                      <Input
                        type="date"
                        value={dob}
                        onChange={(e) => setDob(e.target.value)}
                        className="mt-1"
                        data-testid="verify-input-dob"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">
                        Phone number
                      </Label>
                      <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        className="mt-1"
                        data-testid="verify-input-phone"
                      />
                    </div>
                    <div className="sm:col-span-2 text-eyebrow text-white/60 mt-2">
                      Home address
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-white/60">
                        Street address
                      </Label>
                      <Input
                        value={address.line1}
                        onChange={(e) => setAddr("line1", e.target.value)}
                        placeholder="221B Baker Street"
                        className="mt-1"
                        data-testid="verify-input-line1"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-white/60">
                        Apt / Suite (optional)
                      </Label>
                      <Input
                        value={address.line2}
                        onChange={(e) => setAddr("line2", e.target.value)}
                        placeholder="Suite 12"
                        className="mt-1"
                        data-testid="verify-input-line2"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">City</Label>
                      <Input
                        value={address.city}
                        onChange={(e) => setAddr("city", e.target.value)}
                        className="mt-1"
                        data-testid="verify-input-city"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">
                        State / Region
                      </Label>
                      <Input
                        value={address.state}
                        onChange={(e) => setAddr("state", e.target.value)}
                        className="mt-1"
                        data-testid="verify-input-state"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">
                        Postal code
                      </Label>
                      <Input
                        value={address.postal_code}
                        onChange={(e) =>
                          setAddr("postal_code", e.target.value)
                        }
                        className="mt-1"
                        data-testid="verify-input-postal"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-white/60">Country</Label>
                      <Input
                        value={address.country}
                        onChange={(e) => setAddr("country", e.target.value)}
                        placeholder="United States"
                        className="mt-1"
                        data-testid="verify-input-country"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end pt-2">
                    <Button
                      onClick={() => {
                        const err = validatePersonal();
                        if (err) toast.error(err);
                        else next();
                      }}
                      className="bg-[#B58957] hover:bg-[#9C7548] text-[#0A130E]"
                      data-testid="verify-step0-next"
                    >
                      Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 text-white/85">
                    <IdCard className="w-4 h-4 text-[#B58957]" />
                    <span className="font-display">
                      Upload your government ID
                    </span>
                  </div>
                  <p className="text-sm text-white/60">
                    Passport, driver's licence, or national ID card. The
                    photograph must be clearly visible.
                  </p>
                  <FileUpload
                    value={idDoc}
                    onChange={setIdDoc}
                    label="Government ID (photo of the page with your picture)"
                    testId="verify-upload-id"
                  />
                  <div className="flex justify-between pt-2">
                    <Button
                      variant="ghost"
                      onClick={back}
                      className="text-white/80 hover:bg-white/[0.06]"
                      data-testid="verify-step1-back"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" /> Back
                    </Button>
                    <Button
                      onClick={() => {
                        if (!idDoc?.data) {
                          toast.error("Please upload your ID document.");
                          return;
                        }
                        next();
                      }}
                      className="bg-[#B58957] hover:bg-[#9C7548] text-[#0A130E]"
                      data-testid="verify-step1-next"
                    >
                      Continue <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-5">
                  <div className="flex items-center gap-2 text-white/85">
                    <Camera className="w-4 h-4 text-[#B58957]" />
                    <span className="font-display">Take a selfie</span>
                  </div>
                  <p className="text-sm text-white/60">
                    We'll match your selfie against the photo on your ID. A
                    compliance reviewer approves every application (usually
                    within 24 hours).
                  </p>

                  {selfieMode === "biometric" ? (
                    <>
                      {selfie ? (
                        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex items-center gap-3">
                          <img
                            src={
                              selfie.data.startsWith("data:")
                                ? selfie.data
                                : `data:${selfie.content_type};base64,${selfie.data}`
                            }
                            alt="Selfie"
                            className="w-20 h-20 rounded-lg object-cover"
                          />
                          <div className="flex-1 text-sm text-white/85">
                            <div className="font-medium">Selfie captured</div>
                            <div className="text-white/50 text-xs mt-0.5">
                              Liveness{" "}
                              {livenessPassed ? "passed" : "not captured"}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setSelfie(null);
                              setLivenessFrames([]);
                              setLivenessPassed(false);
                            }}
                            className="text-white/70 hover:bg-white/[0.06]"
                            data-testid="verify-retake-selfie"
                          >
                            Retake
                          </Button>
                        </div>
                      ) : (
                        <BiometricCapture
                          testId="verify-biometric"
                          onComplete={({
                            photo,
                            livenessFrames: frames,
                            livenessPassed: passed,
                          }) => {
                            setSelfie(photo);
                            setLivenessFrames(frames || []);
                            setLivenessPassed(!!passed);
                            toast.success("Selfie captured.");
                          }}
                          onCancel={() => setSelfieMode("upload")}
                        />
                      )}
                      <button
                        type="button"
                        onClick={() => setSelfieMode("upload")}
                        className="text-xs text-[#B58957] hover:underline flex items-center gap-1"
                        data-testid="verify-switch-to-upload"
                      >
                        <Upload className="w-3 h-3" /> Camera not working?
                        Upload a photo instead
                      </button>
                    </>
                  ) : (
                    <>
                      <FileUpload
                        value={selfie}
                        onChange={(v) => {
                          setSelfie(v);
                          setLivenessFrames([]);
                          setLivenessPassed(false);
                        }}
                        label="Upload a clear photo of yourself"
                        accept="image/*"
                        testId="verify-upload-selfie"
                      />
                      <button
                        type="button"
                        onClick={() => setSelfieMode("biometric")}
                        className="text-xs text-[#B58957] hover:underline flex items-center gap-1"
                        data-testid="verify-switch-to-biometric"
                      >
                        <Camera className="w-3 h-3" /> Use camera capture
                        instead
                      </button>
                    </>
                  )}

                  <div className="flex justify-between pt-2">
                    <Button
                      variant="ghost"
                      onClick={back}
                      className="text-white/80 hover:bg-white/[0.06]"
                      data-testid="verify-step2-back"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" /> Back
                    </Button>
                    <Button
                      onClick={submit}
                      disabled={submitting || !selfie?.data}
                      className="bg-[#B58957] hover:bg-[#9C7548] text-[#0A130E]"
                      data-testid="verify-submit"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          Submit for verification{" "}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6 text-center py-6">
                  {kycState?.kyc_status === "approved" ? (
                    <>
                      <CheckCircle2 className="w-14 h-14 text-[#7BBF8E] mx-auto" />
                      <h2 className="font-display text-2xl text-white">
                        You're verified
                      </h2>
                      <p className="text-white/70 max-w-md mx-auto">
                        Auto-approved with{" "}
                        {kycState?.face_match?.similarity?.toFixed(0) || "—"}%
                        face match. Full access unlocked.
                      </p>
                    </>
                  ) : kycState?.kyc_status === "rejected" ? (
                    <>
                      <XCircle className="w-14 h-14 text-[#E27A6A] mx-auto" />
                      <h2 className="font-display text-2xl text-white">
                        Verification declined
                      </h2>
                      <p className="text-white/70 max-w-md mx-auto">
                        Please contact support@finvault.capital.
                      </p>
                    </>
                  ) : (
                    <>
                      <Clock className="w-14 h-14 text-[#B58957] mx-auto" />
                      <h2 className="font-display text-2xl text-white">
                        Under review
                      </h2>
                      <p className="text-white/70 max-w-md mx-auto">
                        Your documents are being reviewed by our compliance
                        team. You'll receive an email when your identity is
                        verified (usually within 24 hours).
                      </p>
                    </>
                  )}
                  <Button
                    onClick={() => navigate("/app")}
                    className="bg-[#B58957] hover:bg-[#9C7548] text-[#0A130E] mt-2"
                    data-testid="verify-step3-continue"
                  >
                    Go to dashboard <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </AppLayout>
  );
}
